/**
 * Totals & calculations: the report's bottom line.
 *
 * Whether there is a totals row, how rows are grouped (a group adds a subtotal), and which
 * columns are calculated and how. A calculation is a chip — "Requested · Total" — so a report
 * that calculates twelve columns reads as twelve short chips, not twelve rows of a form; adding
 * one is a column, a method and Add.
 */
import { useState, type Dispatch, type SetStateAction } from 'react'
import { Sigma, X } from 'lucide-react'
import { COLUMN_DEFS, COLUMN_MAP } from '../columns'
import { AGG_LABELS, aggMethodsFor } from '../reporting'
import { optionClass } from '../studioUi'
import type { AggMethod, ColumnId, ReportConfig } from '../types'
import { DeckPanel, Switch } from './controls'
import { denseField, denseFieldAuto, denseLabel } from './controlStyles'

/** The method a freshly chosen column starts on: a sum for a figure, a count for anything else. */
function defaultMethod(id: ColumnId): AggMethod {
  const def = COLUMN_MAP.get(id)
  return def?.kind === 'number' ? 'sum' : 'count'
}

export function TotalsPanel({
  active,
  config,
  setConfig,
  columns,
}: {
  active: boolean
  config: ReportConfig
  setConfig: Dispatch<SetStateAction<ReportConfig>>
  /** The columns on the report: only these can be calculated. */
  columns: ColumnId[]
}) {
  const defs = columns.map((id) => COLUMN_MAP.get(id)).filter((def) => def != null)
  const calculated = defs.filter((def) => (config.totals[def.id] ?? 'none') !== 'none')
  const uncalculated = defs.filter((def) => (config.totals[def.id] ?? 'none') === 'none')

  const [pickId, setPickId] = useState<ColumnId | ''>('')
  const [pickMethod, setPickMethod] = useState<AggMethod>('sum')

  const setMethod = (id: ColumnId, method: AggMethod) =>
    setConfig((current) => ({ ...current, totals: { ...current.totals, [id]: method } }))

  const pickedDef = pickId ? COLUMN_MAP.get(pickId) : undefined
  const pickMethods = pickedDef ? aggMethodsFor(pickedDef).filter((method) => method !== 'none') : []

  return (
    <DeckPanel
      id="totals"
      title="Totals & calculations"
      icon={<Sigma className="h-3.5 w-3.5" />}
      active={active}
      meta={<span className="tabular-nums">{calculated.length} calculated</span>}
    >
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-end gap-3">
        <Switch
          checked={config.showTotals}
          onChange={(next) => setConfig((current) => ({ ...current, showTotals: next }))}
          label="Totals row"
          title="A grand total, plus a subtotal under each group when rows are grouped"
        />
        <label className="block min-w-0">
          <span className={denseLabel}>Group by · adds subtotals</span>
          <select
            value={config.groupBy}
            onChange={(event) => setConfig((current) => ({ ...current, groupBy: event.target.value as ColumnId | '' }))}
            className={`${denseField} mt-0.5`}
            aria-label="Group report rows"
          >
            <option className={optionClass} value="">
              No grouping
            </option>
            <option className={optionClass} value="stage">
              Stage
            </option>
            {COLUMN_DEFS.filter((column) => column.kind === 'enum' && column.id !== 'stage').map((column) => (
              <option className={optionClass} key={column.id} value={column.id}>
                {column.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div>
        <div
          className={denseLabel}
          title="Count skips missing values. Averages ignore blanks rather than treating them as zero."
        >
          Calculations
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <select
            value={pickId}
            onChange={(event) => {
              const id = event.target.value as ColumnId | ''
              setPickId(id)
              if (id) setPickMethod(defaultMethod(id))
            }}
            className={`${denseFieldAuto} min-w-0 flex-1`}
            aria-label="Column to calculate"
          >
            <option className={optionClass} value="">
              Calculate a column…
            </option>
            {uncalculated.map((def) => (
              <option className={optionClass} key={def.id} value={def.id}>
                {def.label}
              </option>
            ))}
          </select>
          <select
            value={pickMethod}
            onChange={(event) => setPickMethod(event.target.value as AggMethod)}
            disabled={!pickId}
            className={`${denseFieldAuto} w-24 shrink-0`}
            aria-label="Calculation to apply"
          >
            {(pickMethods.length ? pickMethods : (['sum'] as AggMethod[])).map((method) => (
              <option className={optionClass} key={method} value={method}>
                {AGG_LABELS[method]}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!pickId}
            onClick={() => {
              if (!pickId) return
              setMethod(pickId, pickMethod)
              setPickId('')
            }}
            className="shrink-0 rounded border border-amber-300/30 px-2 py-1 text-[10px] text-amber-100 hover:bg-amber-300/10 disabled:opacity-40"
          >
            Add
          </button>
        </div>

        {calculated.length === 0 ? (
          <p className="mt-1.5 text-[10px] text-stone-600">
            No column is calculated, so the totals row is empty.
          </p>
        ) : (
          <ul aria-label="Calculated columns" className="mt-1.5 grid max-h-40 grid-cols-2 content-start gap-1 overflow-y-auto">
            {calculated.map((def) => (
              <li
                key={def.id}
                className="flex min-w-0 items-center gap-1 rounded border border-white/[0.08] bg-black/30 py-0.5 pl-1.5 pr-0.5 text-[10px] text-stone-200"
              >
                <span className="min-w-0 flex-1 truncate" title={def.label}>
                  {def.label}
                </span>
                <select
                  value={config.totals[def.id] ?? 'none'}
                  onChange={(event) => setMethod(def.id, event.target.value as AggMethod)}
                  className="rounded border border-amber-300/20 bg-black/40 px-0.5 py-0 text-[10px] text-amber-100 outline-none [color-scheme:dark] focus:border-amber-300/50"
                  aria-label={`${def.label} total calculation`}
                >
                  {aggMethodsFor(def)
                    .filter((method) => method !== 'none')
                    .map((method) => (
                      <option className={optionClass} key={method} value={method}>
                        {AGG_LABELS[method]}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={() => setMethod(def.id, 'none')}
                  className="rounded p-0.5 text-stone-500 hover:text-red-300"
                  aria-label={`Clear ${def.label} calculation`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DeckPanel>
  )
}
